import React, { useRef, useEffect, useCallback, useState, useMemo } from "react";
import Input from "../../../components/Atomos/Inputs/Input";
import RadioButton from "../../../components/Atomos/Radio/RadioButton";
import WSGCheckbox from "../../../components/Atomos/Checkbox/WSGCheckBox";
import RichTextEditor from "../../../components/SlateEditor/RichTextEditor";
import LegacyButton from "../../../components/Atomos/Buttons/LegacyButton";
import Folder from "../../../components/Atomos/Icons/EditMode/Folder";
import Image from "../../../components/Atomos/Icons/Image";
import Play from "../../../components/Atomos/Icons/Play";
import PlusIcon from "../../../components/Atomos/Icons/PlusIcon";
import Trash from "../../../components/Atomos/Icons/EditMode/Trash";
import DropDownList from "../../../components/Atomos/DropDown/Dropdown";
import PaginatedTable from "../../../components/Organismos/Table/PaginatedTable";
import { useSessionForm } from "../../../hooks/Events/useSessionForm";
import { SpeakerTypeEnum } from "../../../Enums/EventEnums";
import { toastError } from "../../../utils/toast";
import { fetchPersonByFirm } from "../../../Services/Person";
import { getGuestSpeakersByCompany } from "../../../Services/GuestSpeaker";
import { defaultPageSize } from "../../../utils/const";
import { ISorting } from "../../../interfaces/Components/ITable";

interface PostEventTabProps {
  sessionForm: ReturnType<typeof useSessionForm>["sessionForm"];
  updateSessionField: ReturnType<typeof useSessionForm>["updateSessionField"];
  handleUpdatePresenter: ReturnType<typeof useSessionForm>["handleUpdatePostEventPresenter"];
  handleRemovePresenter: ReturnType<typeof useSessionForm>["handleRemovePostEventPresenter"];
  sessionTypeOptions: { id: string; name: string }[];
  speakerTypeOptions: { key: string; value: string }[];
  firmOptions: { id: string; name: string }[];
  companyOptions: { id: string; name: string }[];
  documentTypeOptions: { id: string; name: string }[];
  eventStartDate?: number;
  eventEndDate?: number;
}

const useFilteredProfessionals = (
  selectedFirmId: string | null
) => {
  const [filteredProfessionals, setFilteredProfessionals] = useState<
    { id: string; name: string }[]
  >([]);

  useEffect(() => {
    const fetchProfessionalsByFirm = async () => {
      if (!selectedFirmId) {
        setFilteredProfessionals([]);
        return;
      }

      try {
        const persons = await fetchPersonByFirm(selectedFirmId);
        const formattedData = persons.map((person: any) => ({
          id: person.id,
          name:
            person.displayName ||
            `${person.name || ""} ${person.lastName || ""}`.trim() ||
            "Unknown",
        }));
        setFilteredProfessionals(formattedData);
      } catch (error) {
        console.error("Error fetching professionals by firm:", error);
        setFilteredProfessionals([]);
      }
    };

    fetchProfessionalsByFirm();
  }, [selectedFirmId]);

  return filteredProfessionals;
};

const useFilteredGuestSpeakers = (
  selectedCompanyId: string | null
) => {
  const [filteredGuestSpeakers, setFilteredGuestSpeakers] = useState<
    { id: string; name: string }[]
  >([]);

  useEffect(() => {
    const fetchGuestSpeakersByCompany = async () => {
      if (!selectedCompanyId) {
        setFilteredGuestSpeakers([]);
        return;
      }

      try {
        const guestSpeakers = await getGuestSpeakersByCompany(selectedCompanyId);
        const formattedData = guestSpeakers.map((guestSpeaker: any) => ({
          id: guestSpeaker.id,
          name: `${guestSpeaker.firstName || ""} ${guestSpeaker.lastName || ""}`.trim() || "Unknown",
        }));
        setFilteredGuestSpeakers(formattedData);
      } catch (error) {
        console.error("Error fetching guest speakers by company:", error);
        setFilteredGuestSpeakers([]);
      }
    };

    fetchGuestSpeakersByCompany();
  }, [selectedCompanyId]);

  return filteredGuestSpeakers;
};

const PostEventTab: React.FC<PostEventTabProps> = ({
  sessionForm,
  updateSessionField,
  handleUpdatePresenter,
  handleRemovePresenter,
  speakerTypeOptions,
  firmOptions,
  companyOptions,
  sessionTypeOptions,
  documentTypeOptions,
  eventStartDate,
  eventEndDate,
}) => {
  const sessionImageInputRef = useRef<HTMLInputElement | null>(null);
  const [sort, setSort] = useState<ISorting | undefined>(undefined);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });

  const [formPresenter, setFormPresenter] = useState<{
    personType: keyof typeof SpeakerTypeEnum | null;
    memberFirm: { id: string; name: string } | null;
    professional: { id: string; name: string } | null;
  }>({
    personType: "speaker" as keyof typeof SpeakerTypeEnum,
    memberFirm: null,
    professional: null,
  });

  const selectedFirmId = formPresenter?.memberFirm?.id || null;
  const selectedCompanyId = formPresenter?.personType === "guest" ? selectedFirmId : null;
  const filteredProfessionals = useFilteredProfessionals(
    formPresenter?.personType === "guest" ? null : selectedFirmId
  );
  const filteredGuestSpeakers = useFilteredGuestSpeakers(selectedCompanyId);

  const availableProfessionals = useMemo(() => {
    return filteredProfessionals.filter((professional) => {
      return !sessionForm.postEventPresenters.some(
        (presenter) =>
          presenter.professional?.id === professional.id &&
          presenter.personType === formPresenter.personType
      );
    });
  }, [filteredProfessionals, sessionForm.postEventPresenters, formPresenter.personType]);

  const availableGuestSpeakers = useMemo(() => {
    return filteredGuestSpeakers.filter((guestSpeaker) => {
      return !sessionForm.postEventPresenters.some(
        (presenter) =>
          presenter.professional?.id === guestSpeaker.id &&
          presenter.personType === formPresenter.personType
      );
    });
  }, [filteredGuestSpeakers, sessionForm.postEventPresenters, formPresenter.personType]);

  const handleSavePresenter = useCallback(() => {
    if (!formPresenter.professional) {
      toastError("Please select a Professional before saving.");
      return;
    }

    if (!formPresenter.personType) {
      toastError("Please select a presenter type before saving.");
      return;
    }

    const isDuplicate = sessionForm.postEventPresenters.some(
      (presenter) =>
        presenter.professional?.id === formPresenter.professional?.id &&
        presenter.personType === formPresenter.personType
    );

    if (isDuplicate) {
      toastError("This speaker has already been added with the same type. Please select a different speaker or remove the existing one.");
      return;
    }

    const newPresenter = {
      personType: formPresenter.personType,
      memberFirm: formPresenter.memberFirm,
      professional: formPresenter.professional,
    };
    updateSessionField("postEventPresenters", [...sessionForm.postEventPresenters, newPresenter]);

    setFormPresenter({
      personType: "speaker" as keyof typeof SpeakerTypeEnum,
      memberFirm: null,
      professional: null,
    });
  }, [formPresenter, updateSessionField, sessionForm.postEventPresenters]);

  const parseName = (
    fullName: string | undefined
  ): { firstName: string; lastName: string } => {
    if (!fullName) {
      return { firstName: "", lastName: "" };
    }
    const parts = fullName.trim().split(/\s+/);
    if (parts.length === 1) {
      return { firstName: parts[0], lastName: "" };
    }
    const lastName = parts[parts.length - 1];
    const firstName = parts.slice(0, -1).join(" ");
    return { firstName, lastName };
  };

  const formatPresenterData = useMemo(() => {
    return sessionForm.postEventPresenters.map((presenter, index) => {
      const { firstName, lastName } = parseName(presenter.professional?.name);
      return {
        id: index,
        type: SpeakerTypeEnum[presenter.personType] || "-",
        lastName: lastName || "-",
        firstName: firstName || "-",
        firmCompany: presenter.memberFirm?.name || "-",
      };
    });
  }, [sessionForm.postEventPresenters]);

  const formatDataWithSorting = useCallback(
    (data: any[]) => {
      if (!sort) {
        return data;
      }

      const sorted = [...data].sort((a, b) => {
        const field = sort.field;
        const order = sort.order;
        let aValue: any = a[field as keyof typeof a];
        let bValue: any = b[field as keyof typeof b];

        if (aValue === "-") {
          aValue = "";
        }
        if (bValue === "-") {
          bValue = "";
        }

        if (aValue === "" && bValue !== "") {
          return order === "asc" ? 1 : -1;
        }
        if (aValue !== "" && bValue === "") {
          return order === "asc" ? -1 : 1;
        }

        if (typeof aValue === "string" && typeof bValue === "string") {
          const comparison = aValue.localeCompare(bValue);
          return order === "asc" ? comparison : -comparison;
        }

        if (typeof aValue === "number" && typeof bValue === "number") {
          return order === "asc" ? aValue - bValue : bValue - aValue;
        }

        return 0;
      });

      return sorted.map((item, index) => ({
        ...item,
        number: index + 1,
      }));
    },
    [sort]
  );

  const columnsDef = useMemo(() => [
    {
      id: "number",
      header: "#",
      enableSorting: false,
      cell: ({ row }: { rowIndex: number; row: any; value: any }) => <p>{row.number}</p>,
    },
    {
      id: "type",
      header: "Type",
    },
    {
      id: "lastName",
      header: "Last Name",
    },
    {
      id: "firstName",
      header: "First Name",
    },
    {
      id: "firmCompany",
      header: "Firm / Company",
    },
    {
      id: "actions",
      header: "Actions",
      enableSorting: false,
      cell: ({ row }: { rowIndex: number; row: any; value: any }) => {
        const index = row.id;
        const handleDelete = (e: React.MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          handleRemovePresenter(index);
        };
        return (
          <div
            onClick={handleDelete}
            className="flex items-center gap-2 cursor-pointer text-gray-font"
          >
            <span>Delete</span>
            <div
              className="flex items-center justify-center w-4 h-4"
              style={{ color: "#474F56" }}
            >
              <div className="w-full h-full [&>svg]:w-full [&>svg]:h-full [&>svg>path]:fill-current">
                <Trash />
              </div>
            </div>
          </div>
        );
      },
    },
  ], [handleRemovePresenter]);

  const validateImageFile = (file: File): boolean => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    const maxSize = 25 * 1024 * 1024;
    return validTypes.includes(file.type) && file.size <= maxSize;
  };

  const processImageFile = (file: File) => {
    if (!validateImageFile(file)) {
      alert('Please select a valid image (.jpg, .png, .gif) up to 25 MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const previewUrl = reader.result as string;
      updateSessionField("sessionImage", file);
      updateSessionField("sessionImagePreview", previewUrl);
    };
    reader.readAsDataURL(file);
  };

  const handleImageClick = () => {
    sessionImageInputRef.current?.click();
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processImageFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files[0];
    if (file) {
      processImageFile(file);
    }
  };

  useEffect(() => {
    if (sessionForm.sessionTitle && !sessionForm.postEventTitle) {
      updateSessionField("postEventTitle", sessionForm.sessionTitle);
    }
    if (sessionForm.sessionDate && !sessionForm.postEventDate) {
      updateSessionField("postEventDate", sessionForm.sessionDate);
    }
    if (sessionForm.sessionType && !sessionForm.postEventType) {
      updateSessionField("postEventType", sessionForm.sessionType);
    }
  }, [sessionForm.sessionTitle, sessionForm.sessionDate, sessionForm.sessionType, sessionForm.postEventTitle, sessionForm.postEventDate, sessionForm.postEventType, updateSessionField]);

  return (
    <>
      <div className="flex flex-col gap-6">
        <h3 className="text-base md:text-lg font-medium text-gray-font text-left font-Roboto">
          Post Event Page Setup
        </h3>

        <div>
          <WSGCheckbox
            index={0}
            text="Add Session to Post Event"
            checked={sessionForm.addToPostEvent}
            handleCheckboxChange={() => updateSessionField("addToPostEvent", !sessionForm.addToPostEvent)}
          />
        </div>
      </div>

      <div className="flex flex-col gap-6">

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            title="Title"
            name="postEventTitle"
            value={sessionForm.postEventTitle || sessionForm.sessionTitle || ""}
            onChange={() => {}}
            error={false}
            placeholder=""
            disabled={true}
          />

          <Input
            title="Session Date"
            name="postEventDate"
            value={sessionForm.postEventDate || sessionForm.sessionDate || ""}
            onChange={() => {}}
            error={false}
            placeholder="MM/DD/YYYY"
            disabled={true}
          />

          <Input
            title="Session Type"
            name="postEventType"
            value={sessionForm.postEventType?.name || sessionForm.sessionType?.name || ""}
            onChange={() => {}}
            error={false}
            placeholder="Select Session Type"
            disabled={true}
          />
        </div>
      </div>

      <div className="flex flex-col gap-6">
        <h3 className="text-base md:text-lg font-medium text-gray-font text-left font-Roboto">
          Post Event Description
        </h3>

        <div className="[&_p]:text-left [&_div]:text-left [&_*]:text-left">
          <RichTextEditor
            title=""
            name="postEventDescription"
            value={sessionForm.postEventDescription || sessionForm.sessionDescription}
            onChange={(e) => {
              const value = (e.target as any).value;
              if (Array.isArray(value)) {
                updateSessionField("postEventDescription", value);
              }
            }}
            error={false}
            maxChars={3000}
            characterCount={true}
          />
        </div>
      </div>

      <div className="flex flex-col gap-6">
        <h3 className="text-base md:text-lg font-medium text-gray-font text-left font-Roboto">
          Takeaways
        </h3>

        <div className="flex flex-col gap-4">
          {Array.from({ length: 6 }, (_, index) => {
            const takeaways = sessionForm.takeaways || [];
            const takeaway = takeaways[index] || "";
            return (
              <Input
                key={index}
                title=""
                name={`takeaway-${index}`}
                value={takeaway}
                onChange={(e) => {
                  const newTakeaways = [...takeaways];
                  while (newTakeaways.length < 6) {
                    newTakeaways.push("");
                  }
                  newTakeaways[index] = e.target.value;
                  updateSessionField("takeaways", newTakeaways);
                }}
                error={false}
                placeholder=""
              />
            );
          })}
        </div>
      </div>

      <div className="flex flex-col gap-6">
        <div className="flex items-center gap-4 flex-wrap">
          <h3 className="text-base md:text-lg font-medium text-gray-font text-left font-Roboto">
            YouTube Embed URL
          </h3>
          <p className="text-sm text-gray-600">
            (To add a video search it in YouTube and copy the link of the browser navigation bar)
          </p>
        </div>

        <Input
          title=""
          name="youtubeUrl"
          value={sessionForm.youtubeUrl}
          onChange={(e) => updateSessionField("youtubeUrl", e.target.value)}
          error={false}
          placeholder=""
        />
      </div>

      <div className="flex flex-col gap-3">
        <h3 className="text-base md:text-lg font-medium text-gray-font text-left font-Roboto">
          Session Image
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-[14px]">
          <div className="w-fit">
            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/gif"
              ref={sessionImageInputRef}
              onChange={handleImageChange}
              className="hidden"
            />
            <div
              onClick={handleImageClick}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              className="w-64 aspect-square bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center overflow-hidden cursor-pointer hover:border-blue-400 transition-colors"
            >
              {(sessionForm.sessionImageUrl || sessionForm.sessionImagePreview) ? (
                <img
                  src={sessionForm.sessionImageUrl || sessionForm.sessionImagePreview || ""}
                  alt="Session"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    console.error("Error loading image:", e);
                    e.currentTarget.style.display = "none";
                  }}
                />
              ) : (
                <div className="flex flex-col items-center justify-center text-gray-400">
                  <div className="w-16 h-16 mb-2 fill-gray-400">
                    <Image />
                  </div>
                  <span className="text-xs mb-1">Click or Drag</span>
                  <div className="text-xs text-gray-400 text-center mt-2">
                    <div>25 MB max.</div>
                    <div>426x240 pixels</div>
                    <div>.jpg, .png, .gif</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="w-64 bg-black rounded-lg flex items-center justify-center aspect-square">
            {sessionForm.youtubeUrl ? (
              <div className="w-full h-full flex items-center justify-center">
                <div className="text-white text-center">
                  <p className="text-sm">YouTube video will be embedded here</p>
                  <p className="text-xs text-gray-400 mt-2">{sessionForm.youtubeUrl}</p>
                </div>
              </div>
            ) : (
              <Play className="w-16 h-16 text-red-600" />
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-6">
        <h3 className="text-base md:text-lg font-medium text-gray-font text-left font-Roboto">
          Session Documents
        </h3>

        <div className="flex gap-6">
          {documentTypeOptions.map((option, index) => (
            <RadioButton
              key={option.id}
              index={index}
              text={option.name}
              selected={sessionForm.documentType === option.id}
              handleRadioButtonChange={() => updateSessionField("documentType", option.id)}
              name="documentType"
            />
          ))}
        </div>

        <div className="flex flex-col gap-4">
          <p className="text-xs text-gray-500 text-left">
            .pdf, .pps, .ppsx, .ppt, .pptx - file name must be less than 1000 characters (Maximum 2 documents)
          </p>
          {Array.from({ length: 2 }, (_, index) => {
            const documents = sessionForm.sessionDocuments || [];
            const documentUrls = sessionForm.sessionDocumentUrls || [];
            const currentFile = documents[index];
            const currentUrl = documentUrls[index];

            return (
              <div key={index} className="flex flex-col gap-2">
                <div className="w-fit">
                  <LegacyButton
                    text={`CHOOSE FILE ${index + 1}`}
                    content="textIcon"
                    icon={<div className="w-4 h-4 [&>svg>path]:fill-current ml-2"><Folder /></div>}
                    color="accent"
                    style="filled"
                    size="small"
                    className="!text-xs"
                    onClick={() => {
                      const input = document.createElement("input");
                      input.type = "file";
                      input.accept = ".pdf,.pps,.ppsx,.ppt,.pptx";
                      input.onchange = (e) => {
                        const file = (e.target as HTMLInputElement).files?.[0];
                        if (file) {
                          if (file.name.length > 1000) {
                            alert("File name must be less than 1000 characters");
                            return;
                          }
                          const newDocuments = [...documents];
                          if (index < newDocuments.length) {
                            newDocuments[index] = file;
                          } else {
                            newDocuments.push(file);
                          }
                          updateSessionField("sessionDocuments", newDocuments);
                        }
                      };
                      input.click();
                    }}
                  />
                  {currentFile && (
                    <button
                      type="button"
                      onClick={() => {
                        const newDocuments = documents.filter((_, i) => i !== index);
                        updateSessionField("sessionDocuments", newDocuments);
                      }}
                      className="ml-2 text-red-600 text-xs hover:underline"
                    >
                      Remove
                    </button>
                  )}
                  {currentUrl && !currentFile && (
                    <button
                      type="button"
                      onClick={() => {
                        const newUrls = documentUrls.filter((_, i) => i !== index);
                        updateSessionField("sessionDocumentUrls", newUrls);
                      }}
                      className="ml-2 text-red-600 text-xs hover:underline"
                    >
                      Remove
                    </button>
                  )}
                </div>
                {currentFile && (
                  <p className="text-sm text-gray-700">
                    Selected: {currentFile.name}
                  </p>
                )}
                {currentUrl && !currentFile && (
                  <div className="flex flex-col gap-2">
                    <p className="text-sm text-gray-700">
                      Current document: <a href={currentUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{currentUrl.split('/').pop()}</a>
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex flex-col gap-6">
        <div className="flex justify-between items-center">
          <h2 className="text-[#0070CD] font-decimal text-[32px] font-medium leading-[36px] text-left">
            Post Event Presenters
          </h2>
        </div>

        <div className="space-y-4 bg-white p-4 rounded border">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-font font-Roboto">
              Add New Presenter
            </h3>
          </div>

          <div className="flex gap-6 flex-wrap mb-4">
            {speakerTypeOptions.map((option, optionIndex) => {
              const presenterTypeKey =
                option.key as keyof typeof SpeakerTypeEnum;
              const isSelected = formPresenter?.personType === presenterTypeKey;
              return (
                <RadioButton
                  key={option.key}
                  index={optionIndex}
                  text={option.value}
                  selected={isSelected}
                  handleRadioButtonChange={() => {
                    setFormPresenter((prev) => ({
                      ...prev,
                      personType: presenterTypeKey,
                    }));
                  }}
                  name={`postEventPresenterType-new`}
                />
              );
            })}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DropDownList
              title={
                formPresenter?.personType === "guest"
                  ? "Company"
                  : "Member Firm"
              }
              data={
                formPresenter?.personType === "guest"
                  ? companyOptions
                  : firmOptions
              }
              selectedObjst={formPresenter?.memberFirm?.name}
              seter={(value) => {
                const options =
                  formPresenter?.personType === "guest"
                    ? companyOptions
                    : firmOptions;
                if (typeof value === "string") {
                  const option = options.find(
                    (opt: { id: string; name: string }) => opt.name === value
                  );
                  setFormPresenter((prev) => ({
                    ...prev,
                    memberFirm: option || null,
                    professional: null,
                  }));
                } else if (value && value.id) {
                  setFormPresenter((prev) => ({
                    ...prev,
                    memberFirm: value,
                    professional: null,
                  }));
                } else {
                  setFormPresenter((prev) => ({
                    ...prev,
                    memberFirm: null,
                    professional: null,
                  }));
                }
              }}
              error={false}
              placeholder={
                formPresenter?.personType === "guest"
                  ? "Select Company"
                  : "Select Member Firm"
              }
              getid={true}
            />

            <DropDownList
              title={formPresenter?.personType === "guest" ? "Guest Speaker" : "Professional"}
              data={formPresenter?.personType === "guest" ? availableGuestSpeakers : availableProfessionals}
              selectedObjst={formPresenter?.professional?.name}
              seter={(value) => {
                const options = formPresenter?.personType === "guest" ? availableGuestSpeakers : availableProfessionals;
                if (typeof value === "string") {
                  const option = options.find(
                    (opt: { id: string; name: string }) => opt.name === value
                  );
                  setFormPresenter((prev) => ({
                    ...prev,
                    professional: option || null,
                  }));
                } else if (value && value.id) {
                  setFormPresenter((prev) => ({
                    ...prev,
                    professional: value,
                  }));
                } else {
                  setFormPresenter((prev) => ({ ...prev, professional: null }));
                }
              }}
              error={false}
              placeholder={
                formPresenter?.personType === "guest"
                  ? selectedCompanyId
                    ? "Select Guest Speaker"
                    : "Select Company first"
                  : selectedFirmId
                  ? "Select Professional"
                  : "Select Member Firm first"
              }
              getid={true}
              disabled={formPresenter?.personType === "guest" ? !selectedCompanyId : !selectedFirmId}
            />
          </div>

          <div className="flex justify-end">
            <LegacyButton
              text="ADD"
              content="textIcon"
              icon={
                <div className="w-5 h-5 [&>svg>path]:fill-current ml-2">
                  <PlusIcon />
                </div>
              }
              color="accent"
              style="filled"
              size="small"
              onClick={handleSavePresenter}
            />
          </div>
        </div>

        {formatPresenterData.length > 0 && (
          <PaginatedTable
            data={formatPresenterData || []}
            columnsDef={columnsDef}
            formatData={formatDataWithSorting}
            sorting={sort || { field: "number", order: "asc" }}
            onSortingChange={setSort as any}
            totalCount={formatPresenterData.length || 0}
            pagination={pagination}
            onPaginationChange={setPagination}
            showExcelExport={false}
            showColumnVisibility={false}
          />
        )}
      </div>
    </>
  );
};

export default PostEventTab;

